import { IGetChartPositions } from '../../components/data-containers/PositionsTable/shared/shared';
import getChartPositionsUrl from 'url-constructors/getChartPositionsUrl';
import { IPositionsTableCustomersFetch } from 'components/data-containers/PositionsTable/PositionsTable';
import { mergeCharts } from 'components/data-containers/PositionsTable/shared/hooks/useChartPositions';
import getByUrl from './helper/getByUrl';
import { filterCharts } from 'components/data-containers/PositionsTable/shared/hooks/useFilteredCharts';
import { getUniqueCustomerOptions } from 'components/data-containers/PositionsTable/helper/uniqueCustomersHelpers';
import { getUniqueChartOptions } from 'components/data-containers/PositionsTable/helper/uniqueChartsHelper';

/**
 * Interceptor returns list of unique customers (including the total count of charts aka `positionsCount`)
 *
 * ## Data flow
 * 1. It will fetches all chart positions for daily + weekly.
 * 2. Merge the 2 together in a new combined shape "charts".
 * 3. Filter "charts" by the topic filters (e.g. country).
 * 4. Get the unique customers of all filtered charts and store as new data shape ("unique customer option")
 * 5. Return the list of all unique customer options
 */
const positionsTableCustomersInterceptor = async (
  input: RequestInfo /* eslint-disable-line */,
  init: RequestInit | undefined /* eslint-disable-line */,
  interceptionData: IPositionsTableCustomersFetch
): Promise<Response> => {
  try {
    const {
      entityType,
      entityIds,
      countryIds,
      productType,
      chartType,
      countries,
      positionKey,
      dailyChartType,
      filters,
      customers,
    } = interceptionData;

    const params = {
      entityType,
      entityIds,
      countryIds: filters?.country || countryIds,
      productType,
    };

    const dailyChartPositionsUrl = getChartPositionsUrl({
      ...params,
      chartType: dailyChartType,
      chartFrequency: 'daily',
    });
    const dailyChartPositions = await getByUrl<IGetChartPositions>(
      dailyChartPositionsUrl
    );

    const weeklyChartPositionsUrl = getChartPositionsUrl({
      ...params,
      chartType,
      chartFrequency: 'weekly',
    });
    const weeklyChartPositions = await getByUrl<IGetChartPositions>(
      weeklyChartPositionsUrl
    );

    const charts = mergeCharts({
      dailyChartPositions,
      weeklyChartPositions,
      positionKey,
      countries,
    });

    const filteredCharts = filterCharts({
      charts,
      filters,
    }) as any[];

    const uniqueCustomerOptions = getUniqueCustomerOptions({
      filteredCharts,
      customers,
    });

    const uniqueChartOptions = getUniqueChartOptions({ filteredCharts });
    return new Response(
      JSON.stringify({
        uniqueCustomerOptions,
        dailyChartPositionsUrl,
        weeklyChartPositionsUrl,
        uniqueChartOptions,
      })
    );
  } catch (error) {
    return new Response(JSON.stringify({ reason: new Error(error as any) }));
  }
};

export default positionsTableCustomersInterceptor;
